import { type AxiosInstance } from 'axios'

import { BASE_PATH, type BaseAPI } from './generated/base'
import { type UserSdk } from './sdks/user'
import { type Configuration } from './generated'
import { type TrackingSdk } from './tracking'

export type ControllerApiConstructor<ControllerApi extends BaseAPI> = new (
  ...args: ConstructorParameters<typeof BaseAPI>
) => ControllerApi

export abstract class AbstractSdk<ControllerApi extends BaseAPI = BaseAPI> {
  protected readonly api: ControllerApi

  private readonly configuration: Configuration

  protected abstract getControllerApiConstructor(): ControllerApiConstructor<ControllerApi>

  constructor(
    configuration: Configuration,
    protected readonly userSdk: UserSdk,
    private readonly axios: AxiosInstance,
  ) {
    const ControllerApiConstruct = this.getControllerApiConstructor()
    this.api = new ControllerApiConstruct(configuration, BASE_PATH, axios)
    this.configuration = configuration
    this.configuration.baseOptions = {
      ...this.configuration.baseOptions,
      withCredentials: true,
    }
  }

  // TODO migrer getControllerApiConstructor pour utiliser cette méthode et supprimer le Generic <ControllerApi..>
  create<Type extends BaseAPI = BaseAPI>(ControllerApiConstruct: ControllerApiConstructor<Type>): Type {
    return new ControllerApiConstruct(this.configuration, BASE_PATH, this.axios)
  }
}

export abstract class AbstractSdkWithTracking<
  ControllerApi extends BaseAPI = BaseAPI,
> extends AbstractSdk<ControllerApi> {
  constructor(
    readonly trackingSdk: TrackingSdk,
    ...rest: AbstractSdkConstructorParameters
  ) {
    super(...rest)
  }
}

export type AbstractSdkConstructorParameters = ConstructorParameters<typeof AbstractSdk>
export type AbstractSdkWithTrackingConstructorParameters = ConstructorParameters<typeof AbstractSdkWithTracking>
